<template>
  <modal
    :name="modalName"
    width="520px"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="false"
    @opened="focusClose"
    @before-close="beforeClose"
  >
    <div
      ref="dialog"
      class="automation-delay-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="automation-delay-title"
      @keydown.esc.stop.prevent="$emit('close')"
    >
      <AppModalHeader
        ref="header"
        design="new"
        title="Delay remaining schedule"
        title-id="automation-delay-title"
        @close="$emit('close')"
      />
      <form class="automation-delay-form automation-stack" @submit.prevent="submit">
        <p class="automation-muted">
          Move every remaining planned action and delivery forward. Timezone: {{ timezone }}.
        </p>
        <AppFormField design="new" label="Until" label-for="automation-delay-until" required>
          <AppDatePicker
            v-model="form.until"
            design="new"
            type="datetime"
            format="DD.MM.YYYY HH:mm"
            value-type="YYYY-MM-DDTHH:mm"
            :input-attr="{ id: 'automation-delay-until' }"
            :disabled-date="disabledDate"
            :disabled-time="disabledTime"
            :disabled="saving"
            :error="Boolean(errors.until)"
          />
          <template v-if="errors.until" #error>{{ errors.until }}</template>
        </AppFormField>
        <AppFormField design="new" label="Reason" label-for="automation-delay-reason" required>
          <AppTextarea
            id="automation-delay-reason"
            v-model="form.reason"
            design="new"
            :maxlength="255"
            :disabled="saving"
            :error="Boolean(errors.reason)"
          />
          <template v-if="errors.reason" #error>{{ errors.reason }}</template>
        </AppFormField>
        <p v-if="error" role="alert" class="automation-problem">{{ error }}</p>
        <p v-if="detail && !detail.delay.allowed" class="automation-muted">
          {{ detail.delay.unavailable_reason }}
        </p>
        <div class="automation-actions">
          <AppButton
            ref="submit"
            design="new"
            type="submit"
            aria-label="Apply delay"
            :aria-busy="saving"
            :disabled="!canSubmit"
          >Apply</AppButton>
          <AppButton
            v-if="needsRefresh"
            design="new"
            theme="dark"
            :disabled="saving || refreshing"
            @click.native="$emit('refresh')"
            >Refresh schedule</AppButton
          >
          <AppButton design="new" theme="dark" :disabled="saving" @click.native="$emit('close')"
            >Cancel</AppButton
          >
        </div>
      </form>
    </div>
  </modal>
</template>
<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import { postFollowUpAutomationDelay } from '@/http'
import { delayErrors, delayDateDisabled, delayTimeDisabled, errorMessage } from './model'
export default {
  mixins: [validationMixin],
  props: { runId: Number, detail: Object, revision: String, refreshVersion: Number, refreshing: Boolean },
  data: () => ({
    form: { until: '', reason: '' },
    validationTime: Date.now(),
    error: '',
    saving: false,
    needsRefresh: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }),
  computed: {
    modalName() {
      return `automation-delay-${this.runId}`
    },
    errors() {
      const errors = delayErrors(this.form, this.validationTime, this.detail?.delay.max_days)
      return {
        until: this.$v.form.until.$error ? errors.until : '',
        reason: this.$v.form.reason.$error ? errors.reason : ''
      }
    },
    canSubmit() {
      return Boolean(
        !this.saving &&
          !this.refreshing &&
          !this.needsRefresh &&
          this.revision &&
          this.detail &&
          this.detail.delay.allowed
      )
    }
  },
  validations() {
    return {
      form: {
        until: {
          required,
          withinWindow: value => !delayTimeDisabled(value, this.validationTime, this.detail?.delay.max_days)
        },
        reason: {
          required,
          maxLength: value => maxLength(255)(String(value || '').trim())
        }
      }
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
  },
  watch: {
    saving(value) {
      if (this.$refs.submit) this.$refs.submit.preload = value
    },
    refreshVersion() {
      this.needsRefresh = false
      this.error = ''
    }
  },
  methods: {
    focusClose() {
      this.$refs.header.focusClose()
    },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    },
    disabledDate(value) {
      return delayDateDisabled(value, Date.now(), this.detail?.delay.max_days)
    },
    disabledTime(value) {
      const minute = new Date(value)
      minute.setSeconds(0, 0)
      return delayTimeDisabled(minute, Date.now(), this.detail?.delay.max_days)
    },
    async submit() {
      if (!this.canSubmit) return
      this.validationTime = Date.now()
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      this.saving = true
      this.error = ''
      try {
        await postFollowUpAutomationDelay(this.runId, {
          until: new Date(this.form.until).toISOString(),
          reason: this.form.reason.trim(),
          expected_schedule_revision: this.revision
        })
        if (!this._isDestroyed) this.$emit('saved')
      } catch (error) {
        if (this._isDestroyed) return
        this.error = `${errorMessage(
          error
        )} Refresh the schedule before submitting again. Your form has been kept.`
        this.needsRefresh = true
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
<style scoped lang="scss">
.automation-delay-dialog {
  min-width: 0;
  overflow: hidden;
  border-radius: $ui-overlay-radius;
  background: $ui-surface;
  color: $ui-text;
  font: #{$ui-font-size-body}/ 1.45 $ui-font-body;
}
.automation-delay-form {
  display: grid;
  gap: $ui-space-16;
  padding: $ui-space-20;
}
.automation-delay-form p {
  margin: 0;
}
.automation-delay-form .automation-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-12;
}
.automation-muted {
  color: $ui-muted;
}
.automation-problem {
  color: $ui-red;
}
</style>
